import {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";

const accessibleName = (label, value) => label ? (value ? `${label}: ${value}` : label) : undefined;

const Checkbox = memo(function Checkbox({checked, label, onChange, disabled=false, className=""}) {
    const handleKeyDown = (e) => {
        if(e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.click();
        };
    };

    return (
        <label className={`checkbox-container toggle ${className}`}>
            <input type="checkbox" checked={checked} onChange={onChange} onKeyDown={handleKeyDown} disabled={disabled} tabIndex={0}/>
            <span className="checkbox">
                {checked && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 13l4 4L19 7"/>
                    </svg>
                )}
            </span>
            {label}
        </label>
    );
});

function DateSelect({value, label, placeholder, onChange, disabled=false}) {
    const [isOpen, setIsOpen] = useState(false);
    const [preset, setPreset] = useState(null);
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const dropdownRef = useRef(null);
    const toggleRef = useRef(null);

    const PRESETS = [
        {label: "Last 24 hours", ms: 24 * 60 * 60 * 1000},
        {label: "Last 7 days", ms: 7 * 24 * 60 * 60 * 1000},
        {label: "Last 30 days", ms: 30 * 24 * 60 * 60 * 1000}
    ];

    const today = (() => {
        const d = new Date();
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().split("T")[0];
    })();

    useEffect(() => {
        if(!value) {
            setPreset(null);
            setFrom("");
            setTo("");
        };
    }, [value]);

    useEffect(() => {
        if(!isOpen) return;
        const onPointerDown = (e) => {
            if(dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                const active = document.activeElement;
                if(active && active.type === "date" && dropdownRef.current.contains(active)) return;
                setIsOpen(false);
            };
        };

        document.addEventListener("pointerdown", onPointerDown);
        return() => document.removeEventListener("pointerdown", onPointerDown);
    }, [isOpen]);

    useEffect(() => {
        if(!isOpen) return;
        const onKeyDown = (e) => {
            if(e.key === "Escape") {
                setIsOpen(false);
                toggleRef.current?.focus();
            };
        };

        document.addEventListener("keydown", onKeyDown);
        return() => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen]);

    useEffect(() => {
        if(!isOpen) return;
        const firstOption = dropdownRef.current?.querySelector(".select-dropdown button, .select-dropdown input");
        firstOption?.focus();
    }, [isOpen]);

    const handleBlur = (e) => {
        if(!e.currentTarget.contains(e.relatedTarget)) {
            setIsOpen(false);
        };
    };

    const selectPreset = (p) => {
        const now = new Date();
        const start = new Date(now.getTime() - p.ms);
        setPreset(p.label);
        setFrom("");
        setTo("");
        onChange?.({from: start.toISOString(), to: now.toISOString(), label: p.label});
        setIsOpen(false);
        toggleRef.current?.focus();
    };

    const applyCustom = (nextFrom, nextTo) => {
        setPreset(null);
        setFrom(nextFrom);
        setTo(nextTo);
        if(nextFrom || nextTo) {
            onChange?.({
                from: nextFrom ? new Date(`${nextFrom}T00:00:00`).toISOString() : null,
                to: nextTo ? new Date(`${nextTo}T23:59:59`).toISOString() : null,
                label: "Custom",
            });
        }else {
            onChange?.(null);
        };
    };

    const displayLabel = preset || ((from || to) ? `${from || "…"} → ${to || "…"}` : null);

    return (
        <div className={`dateselect ${isOpen ? "open" : ""}`} ref={dropdownRef} onBlur={handleBlur}>
            <button type="button" ref={toggleRef} onClick={() => setIsOpen((o) => !o)} className={`select-toggle ${isOpen ? "active" : ""} ${disabled ? "disabled" : ""}`} disabled={disabled} aria-label={accessibleName(label, displayLabel)} aria-expanded={isOpen} tabIndex={0}>
                {displayLabel ? (
                    <span>{displayLabel}</span>
                ) : (
                    <span className="placeholder">{placeholder || "Select range"}</span>
                )}
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="dropdown-icon" aria-hidden="true">
                    <path d="M7.017 10.398a.8.8 0 0 1-.563-.234L1.139 4.85a.797.797 0 0 1 1.127-1.095l4.751 4.72 4.752-4.72a.797.797 0 0 1 1.127 1.063L7.58 10.132a.8.8 0 0 1-.564.266" fill="currentColor"/>
                </svg>
            </button>

            {isOpen && (
                <div className="select-dropdown date-dropdown">
                    {PRESETS.map((p) => (
                        <button type="button" key={p.label} onClick={() => selectPreset(p)} className={`option ${preset === p.label ? "selected" : ""}`} tabIndex={0}>
                            {p.label}
                        </button>
                    ))}
                    <div className="date-custom">
                        <label className="date-field">
                            <span>From</span>
                            <input type="date" value={from} max={to || today} onChange={(e) => applyCustom(e.target.value, to)}/>
                        </label>
                        <label className="date-field">
                            <span>To</span>
                            <input type="date" value={to} min={from || undefined} max={today} onChange={(e) => applyCustom(from, e.target.value)}/>
                        </label>
                    </div>
                </div>
            )}
        </div>
    );
};

function MultiSelect({value, label, placeholder, options=[], onChange, disabled=false}) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const toggleRef = useRef(null);

    const selected = useMemo(() => (Array.isArray(value) ? value : []), [value]);

    const toggleOption = useCallback((option) => {
        const next = selected.includes(option)
            ? selected.filter((o) => o !== option)
            : options.filter((o) => selected.includes(o) || o === option);

        onChange(next);
    }, [selected, options, onChange]);

    useEffect(() => {
        if(!isOpen) return;
        const onPointerDown = (e) => {
            if(dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            };
        };

        document.addEventListener("pointerdown", onPointerDown);
        return() => document.removeEventListener("pointerdown", onPointerDown);
    }, [isOpen]);

    useEffect(() => {
        if(!isOpen) return;
        const onKeyDown = (e) => {
            if(e.key === "Escape") {
                setIsOpen(false);
                toggleRef.current?.focus();
            };
        };

        document.addEventListener("keydown", onKeyDown);
        return() => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen]);

    useEffect(() => {
        if(!isOpen) return;
        const firstOption = dropdownRef.current?.querySelector(".select-dropdown button, .select-dropdown input");
        firstOption?.focus();
    }, [isOpen]);

    useEffect(() => {
        if(!isOpen) return;
        const onKeyDown = (e) => {
            if(e.key !== "Tab") return;
            setTimeout(() => {
                if(dropdownRef.current && !dropdownRef.current.contains(document.activeElement)) {
                    setIsOpen(false);
                };
            }, 0);
        };

        document.addEventListener("keydown", onKeyDown);
        return() => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen]);

    return (
        <div className={`multiselect ${isOpen ? "open" : ""}`} ref={dropdownRef}>
            <button type="button" ref={toggleRef} onClick={() => setIsOpen((p) => !p)} className={`select-toggle ${isOpen ? "active" : ""} ${disabled ? "disabled" : ""}`} disabled={disabled} aria-label={accessibleName(label, selected.join(", "))} aria-expanded={isOpen} tabIndex={0}>
                {selected.length > 0 ? (
                    selected.length > 2 ? (
                        <span>{`${selected.length} items selected`}</span>
                    ) : (
                        selected.join(", ")
                    )
                ) : (
                    <span className="placeholder">{placeholder || "Select options"}</span>
                )}
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="dropdown-icon" aria-hidden="true" data-pc-section="triggericon">
                    <path d="M7.017 10.398a.8.8 0 0 1-.563-.234L1.139 4.85a.797.797 0 0 1 1.127-1.095l4.751 4.72 4.752-4.72a.797.797 0 0 1 1.127 1.063L7.58 10.132a.8.8 0 0 1-.564.266" fill="currentColor"/>
                </svg>
            </button>

            {isOpen && (
                <div className="select-dropdown">
                    {options.map((option) => (
                        <Checkbox key={option} checked={selected.includes(option)} onChange={() => toggleOption(option)} label={option} className={`option ${selected.includes(option) ? "selected" : ""}`}/>
                    ))}
                </div>
            )}
        </div>
    );
};

function SingleSelect({value, label, placeholder, options=[], onChange, disabled=false}) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const toggleRef = useRef(null);

    const handleSelectOption = (option) => {
        if(option === value) {
            onChange(null);
        }else {
            onChange(option);
            setIsOpen(false);
            toggleRef.current?.focus();
        };
    };

    const handleBlur = (e) => {
        if(!e.currentTarget.contains(e.relatedTarget)) {
            setIsOpen(false);
        };
    };

    useEffect(() => {
        if(!isOpen) return;
        const onPointerDown = (e) => {
            if(dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            };
        };

        document.addEventListener("pointerdown", onPointerDown);
        return() => document.removeEventListener("pointerdown", onPointerDown);
    }, [isOpen]);

    useEffect(() => {
        if(!isOpen) return;
        const onKeyDown = (e) => {
            if(e.key === "Escape") {
                setIsOpen(false);
                toggleRef.current?.focus();
            };
        };

        document.addEventListener("keydown", onKeyDown);
        return() => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen]);

    useEffect(() => {
        if(!isOpen) return;
        const firstOption = dropdownRef.current?.querySelector(".select-dropdown button, .select-dropdown input");
        firstOption?.focus();
    }, [isOpen]);

    return (
        <div className={`singleselect ${isOpen ? "open" : ""}`} ref={dropdownRef} onBlur={handleBlur}>
            <button type="button" ref={toggleRef} onClick={() => setIsOpen((p) => !p)} className={`select-toggle ${isOpen ? "active" : ""} ${disabled ? "disabled" : ""}`} disabled={disabled} aria-label={accessibleName(label, value)} aria-expanded={isOpen} tabIndex={0}>
                {value ? (
                    <span>{value}</span>
                ) : (
                    <span className="placeholder">{placeholder || "Select option"}</span>
                )}
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="dropdown-icon" aria-hidden="true" data-pc-section="triggericon">
                    <path d="M7.017 10.398a.8.8 0 0 1-.563-.234L1.139 4.85a.797.797 0 0 1 1.127-1.095l4.751 4.72 4.752-4.72a.797.797 0 0 1 1.127 1.063L7.58 10.132a.8.8 0 0 1-.564.266" fill="currentColor"/>
                </svg>
            </button>

            {isOpen && (
                <div className="select-dropdown">
                    {options.map((option) => (
                        <button type="button" key={option} onClick={() => handleSelectOption(option)} className={`option ${value === option ? "selected" : ""}`} tabIndex={0}>
                            {option}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export {Checkbox, DateSelect, MultiSelect, SingleSelect};
